import { NotificationAction } from "@config/local-notifications/notification-actions";
import type { NotificationActionPayloads } from "@config/local-notifications/notification-actions";
import { PushActions } from "@config/push-notifications";
import type { Notification } from "@config/push-notifications";
import {
  CHATS_PATH,
  FEED_PATH,
  MY_ACTIVITY_PATH,
  OPPORTUNITY_PATH,
} from "@shared/helpers/constants";

export const DEFAULT_NOTIFICATION_SENDER_NAME = "מערכת Weme";

export const NOTIFICATION_QUERY_ACTION = "action";
export const NOTIFICATION_ACTION_CANCEL = "cancel";
export const NOTIFICATION_ACTION_SURVEY = "survey";

export function buildOpportunityPath(
  opportunityId: string,
  options?: { action?: string },
): string {
  const base = `${OPPORTUNITY_PATH}/${opportunityId}`;
  if (!options?.action) return base;
  return `${base}?${NOTIFICATION_QUERY_ACTION}=${options.action}`;
}

export function buildChatPath(chatId: string): string {
  return `${CHATS_PATH}/${chatId}`;
}

export function buildFeedPath(opportunityId?: string): string {
  return opportunityId ? `${FEED_PATH}/${opportunityId}` : FEED_PATH;
}

export function parseNotificationNavigateTo(navigateTo: string): URL {
  if (navigateTo.startsWith("http://") || navigateTo.startsWith("https://")) {
    return new URL(navigateTo);
  }
  return new URL(navigateTo, "https://local.invalid");
}

export function resolveNotificationActionBtnText(navigateTo: string): string {
  try {
    const url = parseNotificationNavigateTo(navigateTo);
    const path = url.pathname;
    const action = url.searchParams.get(NOTIFICATION_QUERY_ACTION);

    if (action === NOTIFICATION_ACTION_SURVEY) return "למשוב";
    if (action === NOTIFICATION_ACTION_CANCEL) return "לעדכון או ביטול";
    if (path.startsWith(CHATS_PATH)) return "לצ'אט";
    if (path === FEED_PATH || path.startsWith(`${FEED_PATH}/`)) {
      return "ללוח ההתנדבויות";
    }
    if (path === MY_ACTIVITY_PATH) return "לפעילות שלי";
    if (path.startsWith(OPPORTUNITY_PATH)) return "לפרטים";
    return "לפרטים";
  } catch {
    return "לפרטים";
  }
}

export function clearNotificationActionFromPath(pathWithQuery: string): string {
  try {
    const url = parseNotificationNavigateTo(pathWithQuery);
    url.searchParams.delete(NOTIFICATION_QUERY_ACTION);
    const query = url.searchParams.toString();
    return query ? `${url.pathname}?${query}` : url.pathname;
  } catch {
    return pathWithQuery.split("?")[0] ?? pathWithQuery;
  }
}

export function buildNotificationNavigateActions(
  navigateTo: string | undefined,
): NotificationActionPayloads[] {
  if (!navigateTo) return [];
  return [
    {
      id: NotificationAction.NAVIGATE,
      btnText: resolveNotificationActionBtnText(navigateTo),
      extra: { to: navigateTo },
    },
  ];
}

export function buildNotificationPushMainAction(
  navigateTo: string | undefined,
): Notification["mainAction"] | undefined {
  if (!navigateTo) return undefined;
  return {
    id: PushActions.NAVIGATE,
    extra: { to: navigateTo },
  };
}

export function getNotificationNavigateTarget(actions: unknown): string | undefined {
  if (!Array.isArray(actions)) return undefined;
  const navigateAction = actions.find(
    (action) =>
      action &&
      typeof action === "object" &&
      "id" in action &&
      action.id === NotificationAction.NAVIGATE &&
      "extra" in action &&
      action.extra &&
      typeof action.extra === "object" &&
      "to" in action.extra &&
      typeof action.extra.to === "string",
  );
  return navigateAction?.extra?.to;
}
